
import { z } from "zod";

// Define consistent categories across the application
export const AGENT_CATEGORIES = [
  "Productivity",
  "Creative",
  "Business",
  "Education",
  "Development",
  "Writing",
  "Research",
  "Data Analysis",
  "Marketing",
  "Design",
  "Entertainment",
  "Health"
];

export const LICENSE_OPTIONS = [
  "MIT",
  "GPL",
  "Apache",
  "BSD",
  "Creative Commons",
  "Proprietary",
  "Other"
];

export const PRICING_OPTIONS = [
  { value: "free", label: "Free" },
  { value: "paid", label: "Paid" },
];

// Form validation schema
export const agentFormSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters").max(100, "Name must be less than 100 characters"),
  description: z.string().min(10, "Description must be at least 10 characters").max(250, "Description must be less than 250 characters"),
  category: z.string().min(1, "Please select a category"),
  tags: z.string().refine(tags => {
    const tagArray = tags.split(",").map(tag => tag.trim()).filter(tag => tag !== "");
    return tagArray.length > 0 && tagArray.length <= 10;
  }, { message: "Please provide 1-10 tags, separated by commas" }),
  pricingModel: z.enum(["free", "paid"]),
  price: z.number().optional().refine(price => {
    if (price === undefined) return true;
    return price >= 0;
  }, { message: "Price must be a positive number" }),
  fullDescription: z.string().min(30, "Full description must be at least 30 characters"),
  features: z.string().refine(features => {
    const featureArray = features.split("\n").map(feature => feature.trim()).filter(feature => feature !== "");
    return featureArray.length > 0;
  }, { message: "Please provide at least one feature" }),
  useCases: z.string(),
  requirements: z.string(),
  version: z.string().regex(/^\d+\.\d+\.\d+$/, "Version must be in format X.Y.Z"),
  file: z.any().refine(file => file !== null, { message: "Agent file is required" }),
  isPublic: z.boolean(),
  customCode: z.string().optional(),
  promptTemplate: z.string().optional(),
});

export type AgentFormData = z.infer<typeof agentFormSchema>;

// Helper function to validate the form data
export function validateAgentForm(data: any): { success: boolean; error?: string } {
  try {
    agentFormSchema.parse(data);
    return { success: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: error.errors[0]?.message || "Invalid form data"
      };
    }
    return {
      success: false,
      error: "An unexpected error occurred"
    };
  }
}
